import { isApiError } from '@pa/api'
import { getGetSettingsQueryKey, getSettings, putSettings, refreshFromPf, useGetSettings } from '@pa/api/clinic'
import { toastApiError } from '@pa/staff'
import { useToast } from '@pa/ui'
import { useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { isDirty, rebaseOnRefresh, toPutBody } from './model/draft'
import { SETTINGS_COPY } from './model/messages'
import type { Settings } from './model/types'
import { validate } from './model/validate'
import { warnings } from './model/warnings'

export interface SettingsDraftValue {
  clinicId: string
  /** The saved settings (#23), the base of the draft. */
  base: Settings
  draft: Settings
  dirty: boolean
  problems: string[]
  warnings: string[]
  /** Edit the draft (clinic-owned fields only). The callback gets a copy it may change. */
  update: (fn: (d: Settings) => Settings) => void
  discard: () => void
  save: () => Promise<void>
  saving: boolean
  refresh: () => Promise<void>
  refreshing: boolean
  /** Set after a save refused with `409 settings_changed` (D-34): who saved first, and when. */
  conflict: SettingsConflict | null
  /** The conflict dialog's **Reload**: discard the draft and load the latest saved settings (D-60). */
  reloadLatest: () => Promise<void>
  /** The conflict dialog's **Cancel**: keep the draft (D-60). */
  dismissConflict: () => void
}

/** API.md §1.6 `settings_changed` details, as the C-04 conflict dialog needs them. */
export interface SettingsConflict {
  name: string | null
  updatedAt: string | null
}

function conflictOf(details: Record<string, unknown> | undefined): SettingsConflict {
  const by = details?.updatedBy as { name?: unknown } | null | undefined
  return {
    name: typeof by?.name === 'string' && by.name !== '' ? by.name : null,
    updatedAt: typeof details?.updatedAt === 'string' ? details.updatedAt : null,
  }
}

const Ctx = createContext<SettingsDraftValue | null>(null)

interface DraftState {
  base: Settings
  draft: Settings
}

export function useSettingsDraft(): SettingsDraftValue {
  const value = useContext(Ctx)
  if (!value) throw new Error('useSettingsDraft outside SettingsDraftProvider')
  return value
}

/**
 * Owns the C-04 draft on the `/settings` layout route, so it survives switching tabs (Frontend §6.6). #23 is
 * `staleTime: Infinity`: it changes only through our save or refresh, and is refetched after those, after a 409
 * Reload. A save conflict (D-34) keeps the draft and opens the C-04 conflict dialog (`conflict`, D-60).
 */
export function SettingsDraftProvider({ clinicId, children }: { clinicId: string; children: ReactNode }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const query = useGetSettings(clinicId, { query: { staleTime: Infinity } })
  const [local, setLocal] = useState<DraftState | null>(null)
  const [saving, setSaving] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [conflict, setConflict] = useState<SettingsConflict | null>(null)
  const data = query.data
  // First load, or a refetch while nothing is being edited: follow the saved state.
  const resolve = useCallback(
    (s: DraftState | null): DraftState | null => {
      if (!data) return s
      if (!s || (s.base !== data && !isDirty(s.base, s.draft))) return { base: data, draft: data }
      return s
    },
    [data],
  )
  const state = resolve(local)
  const setState = useCallback(
    (fn: (s: DraftState | null) => DraftState | null) => {
      setLocal((s) => fn(resolve(s)))
    },
    [resolve],
  )

  const setSaved = useCallback(
    (saved: Settings, draft: Settings = saved) => {
      queryClient.setQueryData(getGetSettingsQueryKey(clinicId), saved)
      setLocal({ base: saved, draft })
    },
    [clinicId, queryClient],
  )

  const reload = useCallback(async () => {
    const fresh = await getSettings(clinicId)
    setSaved(fresh)
  }, [clinicId, setSaved])

  const value = useMemo<SettingsDraftValue | null>(() => {
    if (!state) return null
    const { base, draft } = state
    return {
      clinicId,
      base,
      draft,
      dirty: isDirty(base, draft),
      problems: validate(draft),
      warnings: warnings(draft),
      update: (fn) => {
        setState((s) => (s ? { ...s, draft: fn(structuredClone(s.draft)) } : s))
      },
      discard: () => {
        setState((s) => (s ? { ...s, draft: s.base } : s))
      },
      saving,
      save: async () => {
        setSaving(true)
        try {
          const saved = await putSettings(clinicId, toPutBody(draft))
          setSaved(saved)
          toast(SETTINGS_COPY.saved)
        } catch (e) {
          if (isApiError(e) && e.code === 'conflict') {
            setConflict(conflictOf(e.details))
          } else {
            toastApiError(toast, e)
          }
        } finally {
          setSaving(false)
        }
      },
      conflict,
      reloadLatest: async () => {
        setConflict(null)
        try {
          await reload()
        } catch (err) {
          toastApiError(toast, err)
        }
      },
      dismissConflict: () => {
        setConflict(null)
      },
      refreshing,
      refresh: async () => {
        setRefreshing(true)
        try {
          const { settings: fresh, summary } = await refreshFromPf(clinicId)
          setState((s) => {
            const next = s ? rebaseOnRefresh(s.base, s.draft, fresh) : fresh
            return { base: fresh, draft: next }
          })
          queryClient.setQueryData(getGetSettingsQueryKey(clinicId), fresh)
          if (summary.totalAdded || summary.totalRemoved) {
            toast(
              SETTINGS_COPY.synced(summary.totalAdded, summary.totalRemoved),
              summary.totalRemoved ? 'info' : 'success',
            )
          } else {
            toast(SETTINGS_COPY.matches)
          }
        } catch (e) {
          if (isApiError(e) && (e.code === 'pf_unavailable' || e.code === 'pf_error')) {
            toast(SETTINGS_COPY.pfDown, 'error')
          } else if (isApiError(e) && e.code === 'conflict') {
            toast(SETTINGS_COPY.refreshRunning, 'info')
          } else if (isApiError(e) && e.code === 'rate_limited') {
            toast(SETTINGS_COPY.rateLimited(e.retryAfterSec ?? 600), 'error')
          } else {
            toastApiError(toast, e)
          }
        } finally {
          setRefreshing(false)
        }
      },
    }
  }, [state, setState, clinicId, saving, refreshing, conflict, setSaved, reload, toast, queryClient])

  if (query.isError && !state) {
    return (
      <div className="card card-pad" role="alert">
        <p>Couldn&apos;t load the settings.</p>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            void query.refetch()
          }}
        >
          Retry
        </button>
      </div>
    )
  }
  if (!value) return <div className="card card-pad" aria-busy="true" />
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
